import React, {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type DataThemeOptions,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../hooks';

import {
  ANIMATION_BASE_DELAY,
  ANIMATION_DELAY,
  ANIMATION_DURATION,
  ANIMATION_FILL_MODE,
  ANIMATION_TIMING_FUNCTION,
  CHART_DATA_THEMES,
  DARK_THEME_BG_COLOR,
  DEFAULT_CHART_CONFIGS,
  GRADIENT_COLOR_END,
  GRADIENT_COLOR_START,
  MAX_ANGLE_SEGMENTS_COUNT,
  MAX_OFFSET_PERCENTAGE,
  MAX_RADIAL_SEGMENTS_COUNT,
  MIN_ANGLE_SEGMENTS_COUNT,
  MIN_OFFSET_PERCENTAGE,
  MIN_RADIAL_SEGMENTS_COUNT,
  POINT_SVG_BOUNDARY_OFFSET,
  POINT_SVG_COLORS,
  POINT_SVG_SIZES,
  STROKE_WIDTH,
  SVG_PADDING,
  X_AXIS,
  X_AXIS_LABEL_CONFIGS,
  Y_AXIS,
  Y_AXIS_LABEL_CONFIGS,
} from './constant';
import CountUp from './Count';
import { type ChartScales } from './RiskGraph';
import {
  convertPolToCar,
  generateGradient,
  getArrayN,
  getGridStrokeColor,
  getLimitsAdded,
  getScaledValues,
  mapRanges,
} from './utils';

export interface PlotPoint {
  id: string | number;
  xIn: number;
  yIn: number;
  isHollow?: boolean;
  isActive?: boolean;
  category?: string;
  size: 'small' | 'medium' | 'large';
}

interface Sector {
  xLow: number | null;
  xHigh: number | null;
  yLow: number | null;
  yHigh: number | null;
  colStart: string | undefined;
  colEnd: string | undefined;
  sectorCount?: number;
  totalPainted?: number;
  points?: PlotPoint[];
}

type XAxisLabelSpacing =
  (typeof X_AXIS_LABEL_CONFIGS)[keyof typeof X_AXIS_LABEL_CONFIGS];
type YAxisLabelSpacing =
  (typeof Y_AXIS_LABEL_CONFIGS)[keyof typeof Y_AXIS_LABEL_CONFIGS];

interface SemiCircularChartProps {
  radialSegmentCount?: number;
  angleSegmentCount?: number;
  centerGap?: number;
  plotPoints?: PlotPoint[];
  centerCount?: number;
  isSectorInteractive?: boolean;
  onSectorClick?: (sector: unknown) => void;
  onPointClick?: (point: PlotPoint, isAlreadyActive?: boolean) => void;
  isPointHoverDisabled?: boolean;
  chartScales: ChartScales;
  xAxisTitle?: string;
  xAxisLabelSpacing?: XAxisLabelSpacing;
  yAxisTitle?: string;
  yAxisLabelSpacing?: YAxisLabelSpacing;
  isYplotMatching?: boolean;
  colorStart?: string;
  colorEnd?: string;
  secondaryContent?: ReactNode;
  chartSectorClassName?: string;
  chartXAxisClassName?: string;
  chartYAxisClassName?: string;
  chartAreaStyles?: Record<string, unknown>;
  isAnimation?: boolean;
  isProcessing?: boolean;
  isFilterActive?: boolean;
  filterCategories: string[];
  filterPlacement?: number[];
  filterTooltips: string[];
  handleFilterClick?: (value: string) => void;
  dataTheme?: DataThemeOptions;
}

function SemiCircularChart({
  radialSegmentCount,
  angleSegmentCount,
  centerGap,
  plotPoints,
  centerCount,
  isSectorInteractive,
  onSectorClick,
  onPointClick,
  isPointHoverDisabled,
  chartScales,
  xAxisTitle,
  xAxisLabelSpacing,
  yAxisTitle,
  yAxisLabelSpacing,
  isYplotMatching,
  colorStart,
  colorEnd,
  secondaryContent,
  chartAreaStyles,
  chartSectorClassName,
  chartXAxisClassName,
  chartYAxisClassName,
  isAnimation,
  isProcessing,
  isFilterActive,
  filterCategories,
  filterPlacement,
  filterTooltips,
  handleFilterClick,
  dataTheme,
}: SemiCircularChartProps): ReactElement | null {
  const l = useInvertedLocalize();

  const svgRef = useRef<SVGSVGElement>(null);
  const animationRef = useRef(isAnimation);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAnimationRunning, setIsAnimationRunning] = useState(true);

  // Validate Inputs
  const isInputFailed = useMemo(() => {
    const isScaleValid = Object.values(chartScales).every(
      (v) => !Number.isNaN(v)
    );
    const isXAxisSpacingValid = Object.values(X_AXIS_LABEL_CONFIGS).includes(
      xAxisLabelSpacing ?? ''
    );
    const isYAxisSpacingValid = Object.values(Y_AXIS_LABEL_CONFIGS).includes(
      yAxisLabelSpacing ?? ''
    );

    return !isScaleValid || !isXAxisSpacingValid || !isYAxisSpacingValid;
  }, [chartScales, xAxisLabelSpacing, yAxisLabelSpacing]);

  const limitedRadialSegments = getLimitsAdded(
    radialSegmentCount ?? 0,
    MIN_RADIAL_SEGMENTS_COUNT,
    MAX_RADIAL_SEGMENTS_COUNT
  );
  const limitedAngleSegments = getLimitsAdded(
    angleSegmentCount ?? 0,
    MIN_ANGLE_SEGMENTS_COUNT,
    MAX_ANGLE_SEGMENTS_COUNT
  );
  // Calculate dimensions
  const [dimensions, setDimensions] = useState(() => ({
    width: 800,
    height: 640,
    WIDTH: 800,
    HEIGHT: 640,
    radialCount: limitedRadialSegments + 1,
    azimuthCount: limitedAngleSegments + 1,
  }));

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        // Div height and width
        const WIDTH = containerRef.current.clientWidth;
        const HEIGHT = containerRef.current.clientHeight;

        const width = WIDTH - SVG_PADDING;
        const height = Math.min(HEIGHT, width / 2) - SVG_PADDING;

        setDimensions({
          ...dimensions,
          width,
          height,
          WIDTH,
          HEIGHT,
        });
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One time animation
  const isAnimationInEffect = animationRef.current && !isProcessing;

  useEffect(() => {
    const animationTimeOut = setTimeout(
      () => {
        animationRef.current = false;
        setIsAnimationRunning(false);
      },
      3 * ANIMATION_DURATION + ANIMATION_DELAY
    );

    return () => clearTimeout(animationTimeOut);
  }, []);

  // Derived Calculations
  const { width, height, HEIGHT, WIDTH } = dimensions;
  const offset = useMemo(
    () =>
      Math.floor(
        (height *
          getLimitsAdded(
            centerGap ?? 0,
            MIN_OFFSET_PERCENTAGE,
            MAX_OFFSET_PERCENTAGE
          )) /
          100
      ),
    [height, centerGap]
  );
  const radialStep = useMemo(
    () => (height - offset - STROKE_WIDTH) / (dimensions.radialCount - 1),
    [height, offset, dimensions.radialCount]
  );
  const angleStep = useMemo(
    () => 180 / (dimensions.azimuthCount - 1),
    [dimensions.azimuthCount]
  );
  const radialCountMap = useMemo(
    () => getArrayN(dimensions.radialCount),
    [dimensions.radialCount]
  );
  const azimuthCountMap = useMemo(
    () => getArrayN(dimensions.azimuthCount),
    [dimensions.azimuthCount]
  );
  const sectorRadialCountMap = useMemo(
    () => getArrayN(dimensions.radialCount - 1),
    [dimensions.radialCount]
  );
  const sectorAzimuthCountMap = useMemo(
    () => getArrayN(dimensions.azimuthCount - 1),
    [dimensions.azimuthCount]
  );
  // X-Axis plot parameters
  const svgPaddingAdded = useMemo(
    () => SVG_PADDING / dimensions.radialCount,
    [dimensions.radialCount]
  );
  const xAxisWrapperWidth = useMemo(
    () =>
      2 * (radialStep * (dimensions.radialCount - 1) + offset) + SVG_PADDING,
    [dimensions.radialCount, offset, radialStep]
  );
  const xAxisLabelLengths = useMemo(
    () => xAxisWrapperWidth / 2 - (offset + svgPaddingAdded),
    [offset, svgPaddingAdded, xAxisWrapperWidth]
  );
  const xAxisMidlabelLength = useMemo(
    () => (xAxisWrapperWidth - 2 * xAxisLabelLengths) / 2 ** 0.5,
    [xAxisWrapperWidth, xAxisLabelLengths]
  );

  // Sorting points
  const graphPoints = useMemo(() => {
    if (!plotPoints?.length) {
      return [];
    }

    return [...plotPoints]
      .filter((point) => {
        if (
          point.xIn >= chartScales.x_min &&
          point.xIn <= chartScales.x_max &&
          point.yIn >= chartScales.y_min &&
          point.yIn <= chartScales.y_max
        ) {
          return true;
        }

        return false;
      })
      .sort(
        (a, b) =>
          (POINT_SVG_SIZES[b.size] || 0) - (POINT_SVG_SIZES[a.size] || 0)
      );
  }, [plotPoints, chartScales]);

  const handleSectorClick = useCallback(
    (secDim: Sector) => {
      const filteredGraphPoints = graphPoints.filter((point) => {
        if (
          secDim.xLow === null ||
          secDim.yLow === null ||
          secDim.xHigh === null ||
          secDim.yHigh === null
        ) {
          return false;
        }

        if (
          point.xIn >= secDim.xLow &&
          point.xIn <= secDim.xHigh &&
          point.yIn >= secDim.yLow &&
          point.yIn <= secDim.yHigh
        ) {
          return true;
        }

        return false;
      });

      if (onSectorClick) {
        onSectorClick({
          ...secDim,
          sectorCount: filteredGraphPoints.length,
          totalPainted: graphPoints.length,
          points: filteredGraphPoints,
        });
      }
    },
    [graphPoints, onSectorClick]
  );

  const chartSectorClasses = cx(
    'SemiCircularChart-sector',
    isSectorInteractive && 'SemiCircularChart-sector--isActive',
    chartSectorClassName
  );
  const chartXAxisClasses = cx(
    'SemiCircularChart-xAxisTitleWrapper',
    chartXAxisClassName
  );
  const chartYAxisClasses = cx(
    'SemiCircularChart-yAxisPlotNumber',
    chartYAxisClassName
  );

  // Axis number plot
  const xScaleArray = getScaledValues({
    t_min: chartScales.x_min,
    t_max: chartScales.x_max,
    axis: X_AXIS,
    mode: xAxisLabelSpacing,
    segments: limitedRadialSegments,
  });

  const yScaleArray = getScaledValues({
    t_min: chartScales.y_min,
    t_max: chartScales.y_max,
    axis: Y_AXIS,
    mode: yAxisLabelSpacing,
    segments: limitedAngleSegments,
  });

  const getXAxisNumbers = useCallback(
    (isReversed = false) => {
      const styles: Record<
        number,
        { transform: string; left?: string; right?: string }
      > = {};
      const plotLength = xScaleArray.length - 1;

      if (plotLength === 0) {
        return null;
      }

      const step = xAxisLabelLengths / plotLength;
      const loopLimit = plotLength;

      for (let i = 0; i <= loopLimit; i += 1) {
        styles[i] = isReversed
          ? {
              right: `${i * step}px`,
              transform: 'translate(50%, -50%)',
            }
          : {
              left: `${i * step}px`,
              transform: 'translate(-50%, -50%)',
            };
      }

      return xScaleArray.map((value, index) => (
        <div
          key={`xScale-${value}`}
          style={styles[index]}
          className='SemiCircularChart-xAxisPlotNumber'>
          <Typography
            kind='body3'
            desktop='body3-m'
            mobile='body3-m'
            color='--color-foreground-neutral-subtle'
            dataTheme={dataTheme}>
            {value}
          </Typography>
        </div>
      ));
    },
    [xScaleArray, dataTheme, xAxisLabelLengths]
  );

  const getYAxisNumbers = useCallback(() => {
    const styles: Record<
      number,
      { top: string; left: string; transform: string; color?: string }
    > = {};
    const plotLength = yScaleArray.length - 1;

    if (plotLength === 0) {
      return null;
    }

    const cX = WIDTH / 2;
    const cY = HEIGHT;
    const yOffset = isYplotMatching ? 20 : 15;
    const radius = yOffset + xAxisWrapperWidth / 2;
    const step = 180 / plotLength;

    for (let i = 0; i <= plotLength; i += 1) {
      const { x, y } = convertPolToCar(cX, cY, radius, i * step - 180);
      const { color } = generateGradient(
        colorStart ?? '000000',
        colorEnd ?? 'ffffff',
        i * step
      );
      const tilt = mapRanges(i, 0, plotLength, -90, 90);
      const isLeadOrTrail = Boolean(i === 0 || i === plotLength);

      styles[i] = {
        top: `${y}px`,
        left: `${x}px`,
        transform: isLeadOrTrail
          ? `translate(-50%, -62.5%) rotate(${tilt ?? 0}deg)`
          : `translate(-50%, -50%) rotate(${tilt ?? 0}deg)`,
      };

      if (isYplotMatching) {
        styles[i].color = color;
      }
    }

    return yScaleArray.map((value, index) => (
      <div
        key={`yScale-${value}`}
        style={styles[index]}
        className={chartYAxisClasses}>
        <Typography
          kind={isYplotMatching ? 'body1' : 'body3'}
          desktop={isYplotMatching ? 'body1-d' : 'body3-d'}
          mobile={isYplotMatching ? 'body1-m' : 'body3-m'}
          color='--color-foreground-neutral-subtle'
          className={
            isYplotMatching
              ? 'SemiCircularChart-yAxisPlotNumber--isColorMatching'
              : ''
          }
          dataTheme={dataTheme}>
          {value}
        </Typography>
      </div>
    ));
  }, [
    yScaleArray,
    HEIGHT,
    WIDTH,
    chartYAxisClasses,
    colorEnd,
    colorStart,
    dataTheme,
    isYplotMatching,
    xAxisWrapperWidth,
  ]);

  const getYAxisTitle = useCallback(() => {
    const yAxisLeftTitleClassName = cx(
      isYplotMatching
        ? 'SemiCircularChart-yAxisTitleWrapper--isLeftLabelMapped'
        : 'SemiCircularChart-yAxisTitleWrapper--isLeftLabel',
      isYplotMatching && isAnimationInEffect
        ? 'SemiCircularChart-yAxisTitleWrapper--isLeftLabelMappedAndAnimated'
        : 'SemiCircularChart-yAxisTitleWrapper--isLeftLabelAndAnimated'
    );

    const yAxisRightTitleClassName = cx(
      isYplotMatching
        ? 'SemiCircularChart-yAxisTitleWrapper--isRightLabelMapped'
        : 'SemiCircularChart-yAxisTitleWrapper--isRightLabel',
      isYplotMatching && isAnimationInEffect
        ? 'SemiCircularChart-yAxisTitleWrapper--isRightLabelMappedAndAnimated'
        : 'SemiCircularChart-yAxisTitleWrapper--isRightLabelAndAnimated'
    );

    return (
      <>
        <Typography
          kind='body3'
          desktop='body3-m'
          mobile='body3-m'
          color='--color-foreground-neutral-subtle'
          dataTheme={dataTheme}
          className={yAxisLeftTitleClassName}>
          {yAxisTitle}
        </Typography>
        <Typography
          kind='body3'
          desktop='body3-m'
          mobile='body3-m'
          color='--color-foreground-neutral-subtle'
          dataTheme={dataTheme}
          className={yAxisRightTitleClassName}>
          {yAxisTitle}
        </Typography>
      </>
    );
  }, [yAxisTitle, dataTheme, isYplotMatching, isAnimationInEffect]);

  const getFilters = useCallback(() => {
    const stepAngle = 180 / (filterCategories.length + 1);
    const isPlacementAvailable =
      Array.isArray(filterPlacement) && filterPlacement.length > 0;
    const filterAngles: number[] = isPlacementAvailable ? filterPlacement : [];

    if (!isPlacementAvailable) {
      for (let i = stepAngle; i < 180; i += stepAngle) {
        filterAngles.push(i);
      }
    }

    return filterCategories.map((category, index) => {
      const { x, y } = convertPolToCar(
        WIDTH / 2,
        HEIGHT,
        offset + radialStep * dimensions.radialCount,
        -filterAngles[index]
      );
      const { color } = generateGradient('', '', 180 - filterAngles[index]);

      const filtertextClasses = cx(
        'SemiCircularChart-filterWrapper--filterItemText',
        isAnimationInEffect &&
          'SemiCircularChart-filterWrapper--filterItemText--isAnimated'
      );

      return (
        <div
          key={category}
          style={{
            position: 'absolute',
            top: `${y}px`,
            left: `${x}px`,
            transform: `translate(-50%, -50%)`,
            color,
          }}
          className='SemiCircularChart-filterWrapper--filterItem'>
          <Tooltip
            placement='top'
            trigger={
              <div
                style={{
                  display: 'block',
                  padding: filterAngles[index] === 90 ? 'none' : '0.75rem',
                  borderRadius: '50%',
                  transform: `rotate(${90 - filterAngles[index]}deg)`,
                  pointerEvents: isProcessing ? 'none' : 'initial',
                }}
                tabIndex={0}
                onClick={(e) => {
                  if (
                    e.detail === 1 &&
                    (isFilterActive || isProcessing) &&
                    handleFilterClick
                  ) {
                    handleFilterClick(category);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    isFilterActive &&
                    handleFilterClick
                  ) {
                    handleFilterClick(category);
                  }
                }}
                role='button'
                onMouseEnter={() => {
                  if (!isFilterActive || isProcessing) {
                    return;
                  }

                  if (animationRef.current) {
                    animationRef.current = false;
                  }

                  const targetPoints =
                    svgRef.current?.querySelectorAll(
                      `.SemiCircularChart-plotPoint:not([data-category="${category}"])`
                    ) ?? [];

                  targetPoints.forEach((element) => {
                    (element as SVGSVGElement).classList.remove(
                      'SemiCircularChart-plotPoint--isVisible'
                    );
                    (element as SVGSVGElement).classList.add(
                      'SemiCircularChart-plotPoint--isHidden'
                    );
                  });
                }}
                onMouseLeave={() => {
                  if (!isFilterActive || isProcessing) {
                    return;
                  }

                  const targetPoints =
                    svgRef.current?.querySelectorAll(
                      `.SemiCircularChart-plotPoint:not([data-category="${category}"])`
                    ) ?? [];

                  targetPoints.forEach((element) => {
                    (element as SVGSVGElement).classList.remove(
                      'SemiCircularChart-plotPoint--isHidden'
                    );
                    (element as SVGSVGElement).classList.add(
                      'SemiCircularChart-plotPoint--isVisible'
                    );
                  });
                }}
                data-category={category}>
                <Typography
                  kind='body1'
                  desktop='body1-m'
                  mobile='body1-m'
                  className={filtertextClasses}>
                  {l(category.toUpperCase())}
                </Typography>
              </div>
            }
            text={`${filterTooltips[index]}`}
            dataTheme='dark'
          />
        </div>
      );
    });
  }, [
    l,
    offset,
    HEIGHT,
    WIDTH,
    dimensions.radialCount,
    radialStep,
    isFilterActive,
    filterPlacement,
    filterCategories,
    filterTooltips,
    isAnimationInEffect,
    isProcessing,
    handleFilterClick,
  ]);

  if (isInputFailed) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        pointerEvents: isAnimationRunning ? 'none' : 'auto',
      }}
      className='SemiCircularChart-chartAreaAndxAxisWrapper'>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          ...chartAreaStyles,
        }}>
        <svg
          ref={svgRef}
          width={width + SVG_PADDING}
          height={height + SVG_PADDING}
          viewBox={`0 ${-1 - SVG_PADDING / 4} ${width} ${height}`}
          preserveAspectRatio='xMidYMid meet'>
          {/* Draw concentric semicircles */}
          <g>
            {radialCountMap.map((circleCount, index) => {
              const radius = radialStep * (circleCount - 1) + offset;
              const startX = width / 2 - radius;
              const startY = height;
              const endX = width / 2 + radius;
              const endY = height;

              // SVG path for a semicircle (using arc command)
              const d = `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;

              return (
                <path
                  key={`radial-circle-${circleCount}`}
                  d={d}
                  stroke={getGridStrokeColor(dataTheme ?? 'dark')}
                  strokeWidth={STROKE_WIDTH}
                  fill='none'
                  style={
                    isAnimationInEffect
                      ? {
                          animationDelay: `${
                            ANIMATION_BASE_DELAY +
                            ANIMATION_DURATION +
                            (index * ANIMATION_DELAY) / 3
                          }ms`,
                          animationDuration: `${ANIMATION_DURATION}ms`,
                          animationFillMode: ANIMATION_FILL_MODE,
                          animationTimingFunction: ANIMATION_TIMING_FUNCTION,
                          transformOrigin: 'bottom center',
                        }
                      : {}
                  }
                  className={`${
                    isAnimationInEffect
                      ? 'SemiCircularChart-concentricCircle'
                      : ''
                  }`}
                />
              );
            })}
          </g>
          {/* Draw vertical lines from inner to outer semicircles */}
          <g>
            {azimuthCountMap.map((pLineCount, index) => {
              const isCornorAzimuth = Boolean(
                index === 0 || index === azimuthCountMap.length - 1
              );

              const angle = (pLineCount - 1) * angleStep;

              // Calculate x, y coordinates for the start (outermost circle)
              const x1 =
                width / 2 +
                (radialStep * (dimensions.radialCount - 1) + offset) *
                  Math.cos((angle * Math.PI) / 180);
              const y1 =
                height -
                (radialStep * (dimensions.radialCount - 1) + offset) *
                  Math.sin((angle * Math.PI) / 180);

              // Calculate x, y coordinates for the end (innermost circle)
              const x2 = width / 2 + offset * Math.cos((angle * Math.PI) / 180);
              const y2 = height - offset * Math.sin((angle * Math.PI) / 180);

              return (
                <line
                  key={`azimuth-cout-${pLineCount}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={getGridStrokeColor(dataTheme ?? 'dark')}
                  strokeWidth={
                    isCornorAzimuth ? 2 * STROKE_WIDTH : STROKE_WIDTH
                  }
                  style={
                    isAnimationInEffect
                      ? {
                          animationDelay: `${
                            ANIMATION_BASE_DELAY +
                            (dimensions.azimuthCount - index + 1) *
                              ANIMATION_DELAY
                          }ms`,
                          animationDuration: `${ANIMATION_DURATION}ms`,
                          animationFillMode: ANIMATION_FILL_MODE,
                          animationTimingFunction: ANIMATION_TIMING_FUNCTION,
                          transformOrigin: `${x2}px ${y2}px`,
                        }
                      : {}
                  }
                  className={`${
                    isAnimationInEffect ? 'SemiCircularChart-azimuth' : ''
                  }`}
                />
              );
            })}
          </g>
          {/* Draw sectors with hover effect */}
          {isSectorInteractive && (
            <g>
              {sectorRadialCountMap.map((sectorRadialCount) => (
                <g key={`sector-radial-count-${sectorRadialCount}`}>
                  {sectorAzimuthCountMap.map((sectorAzimuthCount) => {
                    // Base params
                    const cX = width / 2;
                    const cY = height;
                    const rInn = radialStep * (sectorRadialCount - 1) + offset;
                    const rOut = radialStep * sectorRadialCount + offset;
                    const angleLead = -1 * sectorAzimuthCount * angleStep;
                    const angleTrail = (1 - sectorAzimuthCount) * angleStep;

                    // Shifted base params for sector
                    const rInnSh = rInn + STROKE_WIDTH;
                    const rOutSh = rOut - STROKE_WIDTH;
                    const angLeadSh =
                      angleLead + (180 * STROKE_WIDTH) / rOut / Math.PI;
                    const angTrailSh =
                      angleTrail - (180 * STROKE_WIDTH) / rOut / Math.PI;

                    // Polar points
                    const outerStart = convertPolToCar(
                      cX,
                      cY,
                      rOutSh,
                      angLeadSh
                    );
                    const outerEnd = convertPolToCar(
                      cX,
                      cY,
                      rOutSh,
                      angTrailSh
                    );
                    const innerEnd = convertPolToCar(
                      cX,
                      cY,
                      rInnSh,
                      angTrailSh
                    );
                    const innerStart = convertPolToCar(
                      cX,
                      cY,
                      rInnSh,
                      angLeadSh
                    );

                    // SVG path calculation
                    const arcAngle = angleStep <= 180 ? 0 : 1;
                    const d = [
                      `M ${outerStart.x} ${outerStart.y}`, // Move to outer start
                      `A ${rOut} ${rOut} 0 ${arcAngle} 1 ${outerEnd.x} ${outerEnd.y}`, // Outer arc
                      `L ${innerEnd.x} ${innerEnd.y}`, // Line to inner arc start
                      `A ${rInn} ${rInn} 0 ${arcAngle} 0 ${innerStart.x} ${innerStart.y}`, // Inner arc
                      'Z',
                    ].join(' ');

                    return (
                      <path
                        key={`sector-both-count-${sectorRadialCount}-${sectorAzimuthCount}`}
                        d={d}
                        stroke='none'
                        className={chartSectorClasses}
                        onClick={() => {
                          // Filter points
                          const rMin = offset;
                          const rMax =
                            radialStep * (dimensions.radialCount - 1) + offset;
                          const xLow = mapRanges(
                            rInn,
                            rMin,
                            rMax,
                            chartScales.x_min,
                            chartScales.x_max
                          );
                          const xHigh = mapRanges(
                            rOut,
                            rMin,
                            rMax,
                            chartScales.x_min,
                            chartScales.x_max
                          );
                          const yLow = mapRanges(
                            180 + angleLead,
                            0,
                            180,
                            chartScales.y_min,
                            chartScales.y_max
                          );
                          const yHigh = mapRanges(
                            180 + angleTrail,
                            0,
                            180,
                            chartScales.y_min,
                            chartScales.y_max
                          );
                          const { color: colStart } = generateGradient(
                            colorStart ?? '000000',
                            colorEnd ?? 'ffffff',
                            180 + angleLead
                          );
                          const { color: colEnd } = generateGradient(
                            colorStart ?? '000000',
                            colorEnd ?? 'ffffff',
                            180 + angleTrail
                          );

                          handleSectorClick({
                            xLow,
                            xHigh,
                            yLow,
                            yHigh,
                            colStart,
                            colEnd,
                          });
                        }}
                      />
                    );
                  })}
                </g>
              ))}
            </g>
          )}
          {/* Plot graph points */}
          <g>
            {graphPoints.map((point) => {
              const { xIn, yIn, isHollow, isActive, size, category } = point;

              const cRad = POINT_SVG_SIZES[size] || POINT_SVG_SIZES.small;
              const cX = width / 2;
              const cY = height;
              const rMin = offset;
              const rMax = radialStep * (dimensions.radialCount - 1) + offset;
              const r = mapRanges(
                xIn,
                chartScales.x_min,
                chartScales.x_max,
                rMin,
                rMax
              );
              const yOut = mapRanges(
                yIn,
                chartScales.y_min,
                chartScales.y_max,
                0,
                180
              );
              const boundaryOffset =
                POINT_SVG_BOUNDARY_OFFSET[size] ||
                POINT_SVG_BOUNDARY_OFFSET.small;

              if (r === null || yOut === null) {
                return null;
              }

              const angle = 180 - yOut;

              const { x, y } = convertPolToCar(cX, cY, r, -angle);
              const { color } = generateGradient(
                colorStart ?? '000000',
                colorEnd ?? 'fffffff',
                180 - Math.round(angle)
              );

              return (
                <g
                  onClick={(e) => {
                    e.stopPropagation();

                    if (
                      !isProcessing &&
                      !isPointHoverDisabled &&
                      onPointClick
                    ) {
                      onPointClick(point, isActive);
                    }
                  }}
                  style={
                    isAnimationInEffect
                      ? {
                          animationDelay: `${
                            ANIMATION_BASE_DELAY + 2 * ANIMATION_DURATION
                          }ms`,
                          animationDuration: `${ANIMATION_DURATION}ms`,
                          animationFillMode: ANIMATION_FILL_MODE,
                          animationTimingFunction: ANIMATION_TIMING_FUNCTION,
                          transformOrigin: 'center',
                        }
                      : {}
                  }
                  data-category={category}
                  className={`SemiCircularChart-plotPoint ${
                    isAnimationInEffect
                      ? 'SemiCircularChart-plotPoint--isAnimated'
                      : ''
                  } ${
                    !isPointHoverDisabled
                      ? 'SemiCircularChart-plotPoint--isHoverable'
                      : ''
                  }`}>
                  {!isPointHoverDisabled && (
                    <circle
                      cx={x}
                      cy={y}
                      r={cRad + 2 * boundaryOffset}
                      fill={
                        isActive
                          ? POINT_SVG_COLORS.ACTIVE_COLOR
                          : POINT_SVG_COLORS.HOVER_COLOR
                      }
                      className={
                        isActive && !isProcessing
                          ? ''
                          : 'SemiCircularChart-plotPointBorder'
                      }
                    />
                  )}
                  {isActive && (
                    <circle
                      cx={x}
                      cy={y}
                      r={cRad + boundaryOffset}
                      fill={POINT_SVG_COLORS.BORDER_COLOR}
                    />
                  )}
                  <circle cx={x} cy={y} r={cRad} fill={color} />
                  {isHollow && (
                    <circle
                      cx={x}
                      cy={y}
                      r={cRad - boundaryOffset}
                      fill={POINT_SVG_COLORS.HOLLOW_COLOR}
                    />
                  )}
                </g>
              );
            })}
          </g>
          {/* For in processing state */}
          {(() => {
            if (isProcessing) {
              const radius = radialStep * (dimensions.radialCount - 1) + offset;
              const startX = width / 2 - radius;
              const startY = height;
              const endX = width / 2 + radius;
              const endY = height;

              // SVG path for a semicircle (using arc command)
              const d = `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;

              return (
                <path
                  d={d}
                  stroke='var(--color-border-neutral-subtlest, #4A4C59)'
                  strokeWidth={STROKE_WIDTH}
                  fill='rgba(255, 255, 255, 0.12)'
                />
              );
            }

            return null;
          })()}
          {/* Gradient fill and the first semicircle */}
          {(() => {
            const gradOffset = 20;
            const radius = offset - STROKE_WIDTH / 2;
            const startX = width / 2 - radius;
            const startY = height;
            const endX = width / 2 + radius;
            const endY = height;
            const cX = width / 2;
            const cY = height;

            return (
              <g>
                <path
                  d={`M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`}
                  fill={DARK_THEME_BG_COLOR}
                />
                <line
                  x1={startX}
                  y1={startY}
                  x2={endX}
                  y2={endY}
                  fill={DARK_THEME_BG_COLOR}
                  strokeWidth={STROKE_WIDTH}
                />
                <path
                  d={`M ${startX + gradOffset} ${startY} A ${
                    radius - gradOffset
                  } ${radius - gradOffset} 0 0 1 ${endX - gradOffset} ${endY}`}
                  fill='transparent'
                  stroke='url(#curvedPipe)'
                  strokeWidth='20'
                  strokeMiterlimit='0'
                  strokeDasharray='3.63 3.63'
                />
                <defs>
                  <linearGradient
                    id='curvedPipe'
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    gradientUnits='userSpaceOnUse'>
                    <stop
                      stopColor={graphPoints.length > 0 ? '#FF6F26' : '#5C5E6E'}
                    />
                    <stop
                      offset='1'
                      stopColor={graphPoints.length > 0 ? '#FF66BA' : '#5C5E6E'}
                    />
                  </linearGradient>
                </defs>
                {isAnimationInEffect && (
                  <path
                    d={`M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`}
                    fill={DARK_THEME_BG_COLOR}
                    style={{
                      animationDelay: `${
                        ANIMATION_BASE_DELAY +
                        2 * ANIMATION_DURATION +
                        (dimensions.radialCount * ANIMATION_DELAY) / 3
                      }ms`,
                      animationDuration: `${ANIMATION_DURATION}ms`,
                      animationFillMode: ANIMATION_FILL_MODE,
                      animationTimingFunction: ANIMATION_TIMING_FUNCTION,
                      transformOrigin: `${cX}px ${cY}px`,
                    }}
                    className='SemiCircularChart-stepper'
                  />
                )}
              </g>
            );
          })()}
        </svg>
        {/* Y Axis plot */}
        {graphPoints.length > 0 && getYAxisNumbers()}
        {/* Y Axis Title */}
        {getYAxisTitle()}
        {/* Filter categories */}
        {getFilters()}
      </div>
      {/* X Axis plot */}
      <div
        style={{ width: `${xAxisWrapperWidth}px` }}
        className={chartXAxisClasses}>
        <div
          style={{ width: `${xAxisLabelLengths}px` }}
          className='SemiCircularChart-xAxisTitleWrapper--isLeftLabel'>
          {isAnimationInEffect && (
            <div className='SemiCircularChart-xAxisTitleAnimator--isLeft' />
          )}
          <div
            className={`SemiCircularChart-xAxisPlotNumberRange ${
              isAnimationInEffect
                ? 'SemiCircularChart-xAxisPlotNumberRange--isAnimated'
                : ''
            }`}>
            {graphPoints.length > 0 && getXAxisNumbers(true)}
          </div>
          <Typography
            kind='body3'
            desktop='body3-m'
            mobile='body3-m'
            color='--color-foreground-neutral-subtle'
            dataTheme={dataTheme}>
            {xAxisTitle}
          </Typography>
        </div>
        <div
          style={{
            width: `calc(${xAxisMidlabelLength}px - 1rem)`,
            animationDelay: isAnimationInEffect
              ? `${
                  ANIMATION_BASE_DELAY +
                  (dimensions.azimuthCount + 1) * ANIMATION_DELAY
                }ms`
              : 'none',
          }}
          className={`SemiCircularChart-utility--isMiddleLabel ${
            isAnimationInEffect
              ? 'SemiCircularChart-utility--isMiddleLabelAndAnimated'
              : ''
          }`}>
          {isProcessing ? (
            <Loader kind='loop' format='main' color='inverse' />
          ) : (
            <>
              <Tooltip
                placement='top'
                trigger={
                  <>
                    <CountUp
                      target={centerCount}
                      delay={
                        ANIMATION_BASE_DELAY +
                        2 * ANIMATION_DURATION +
                        (dimensions.radialCount * ANIMATION_DELAY) / 3
                      }
                      duration={ANIMATION_DURATION}
                      className={
                        dataTheme === CHART_DATA_THEMES.DARK
                          ? 'SemiCircularChart-utilityCount--isDark'
                          : 'SemiCircularChart-utilityCount--isLight'
                      }
                      isAnimation={isAnimationInEffect}
                      isSecondary={Boolean(secondaryContent)}
                    />
                    <Typography
                      kind='body3'
                      desktop='body3-d'
                      mobile='body3-d'
                      color='--color-foreground-neutral-bold'
                      className='SemiCircularChart-utilityText'
                      dataTheme={dataTheme}>
                      {l('VIOLATIONS')}
                    </Typography>
                  </>
                }
                text={String(centerCount ?? graphPoints.length)}
                dataTheme='dark'
              />
              {secondaryContent}
            </>
          )}
        </div>
        <div
          style={{ width: `${xAxisLabelLengths}px` }}
          className='SemiCircularChart-xAxisTitleWrapper--isRightLabel'>
          {isAnimationInEffect && (
            <div className='SemiCircularChart-xAxisTitleAnimator--isRight' />
          )}
          <div
            className={`SemiCircularChart-xAxisPlotNumberRange ${
              isAnimationInEffect
                ? 'SemiCircularChart-xAxisPlotNumberRange--isAnimated'
                : ''
            }`}>
            {graphPoints.length > 0 && getXAxisNumbers()}
          </div>
          <Typography
            kind='body3'
            desktop='body3-m'
            mobile='body3-m'
            color='--color-foreground-neutral-subtle'
            dataTheme={dataTheme}>
            {xAxisTitle}
          </Typography>
        </div>
      </div>
    </div>
  );
}

SemiCircularChart.defaultProps = {
  radialSegmentCount: DEFAULT_CHART_CONFIGS.RADIAL,
  angleSegmentCount: DEFAULT_CHART_CONFIGS.AZIMUTH,
  centerGap: DEFAULT_CHART_CONFIGS.CENTER,
  plotPoints: [],
  centerCount: 0,
  isSectorInteractive: true,
  onSectorClick: () => {},
  onPointClick: () => {},
  isPointHoverDisabled: false,
  xAxisTitle: 'X axis title',
  xAxisLabelSpacing: X_AXIS_LABEL_CONFIGS.FULL,
  yAxisTitle: 'Y axis title',
  yAxisLabelSpacing: Y_AXIS_LABEL_CONFIGS.FULL,
  isYplotMatching: false,
  colorStart: GRADIENT_COLOR_START,
  colorEnd: GRADIENT_COLOR_END,
  secondaryContent: null,
  chartAreaStyles: {},
  chartSectorClassName: '',
  chartXAxisClassName: '',
  chartYAxisClassName: '',
  isAnimation: true,
  isProcessing: false,
  isFilterActive: true,
  filterPlacement: [30, 90, 150],
  handleFilterClick: () => {},
  dataTheme: CHART_DATA_THEMES.DARK,
};

export default memo(SemiCircularChart);
